import * as React from 'react';

import { useModulesData } from '@/features/modules/api/queries';
import { useProfilesData } from '@/features/user/api';
import { useIsMobile } from '@/lib/hooks';

import { ClosedView } from './components/closed-view';
import { OpenView } from './components/open-view';
import { API_CONSTANTS } from './constants';
import { useBhanzuBuddyEffects } from './hooks/use-bhanzu-buddy-effects';
import { useBhanzuBuddyGuideFlow } from './hooks/use-bhanzu-buddy-guide-flow';
import { useBhanzuBuddyHandlers } from './hooks/use-bhanzu-buddy-handlers';
import { useBhanzuBuddyModeHandlers } from './hooks/use-bhanzu-buddy-mode-handlers';
import { useBhanzuBuddyQuickChallengeHandlers } from './hooks/use-bhanzu-buddy-quick-challenge-handlers';
import { useBhanzuBuddyState } from './hooks/use-bhanzu-buddy-state';
import { useBhanzuBuddySwitchToGuide } from './hooks/use-bhanzu-buddy-switch-to-guide';
import { useBhanzuBuddyToggleOpen } from './hooks/use-bhanzu-buddy-toggle-open';
import { useBhanzuBuddyUserInteraction } from './hooks/use-bhanzu-buddy-user-interaction';
import { useBhanzuBuddyVoice } from './hooks/use-bhanzu-buddy-voice';
import { useInitialHistoryLoader } from './hooks/use-initial-history-loader';
import { useOptionalBhanzuBuddyChat } from './hooks/use-optional-bhanzu-buddy-chat';
import { useQuickChallenge } from './hooks/use-quick-challenge';
import { useDraggable } from './use-draggable';
import {
  getEffectiveInitialPosition,
  isBraveBrowser,
} from './utils/buddy-helpers';
import { ensureLatexStylesInjected } from './utils/latex-styles';

ensureLatexStylesInjected();

export interface BhanzuBuddyProps extends React.HTMLAttributes<HTMLDivElement> {
  placeholder?: string;
  disabled?: boolean;
  initialPosition?: { x: number; y: number };
  leadId?: string;
  source?: string;
  onMessageSent?: (response: any) => void;
  onError?: (error: any) => void;
  isOpen?: boolean;
  onToggleOpen?: (isOpen: boolean) => void;
  defaultMode?: 'explain' | 'guide';
  enableQuickChallenge?: boolean;
}

const BhanzuBuddy = React.forwardRef<HTMLDivElement, BhanzuBuddyProps>(
  (
    {
      className,
      placeholder = 'Type here or talk...',
      disabled = false,
      initialPosition,
      leadId,
      source: initialSource = API_CONSTANTS.DEFAULT_SOURCE,
      onMessageSent,
      onError,
      isOpen: controlledIsOpen,
      onToggleOpen,
      defaultMode = 'explain',
      enableQuickChallenge = true,
      ...props
    },
    ref
  ) => {
    const isControlledComp =
      controlledIsOpen !== undefined || onToggleOpen !== undefined;
    const isMobile = useIsMobile();

    const {
      chatState,
      setChatState,
      currentMode,
      setCurrentMode,
      currentSource,
      setCurrentSource,
      message,
      setMessage,
      quickChallengeContextRef,
      prevMessageRef,
      chatContainerRef,
      chatInterfaceRef,
      hasScrolledOnOpen,
      location,
      hasUserInteracted,
      setHasUserInteracted,
      orientation,
      showQuickChallenge,
      setShowQuickChallenge,
      quickChallengeSnoozeUntil,
      setQuickChallengeSnoozeUntil,
      isOpen,
      setInternalIsOpen,
    } = useBhanzuBuddyState({
      isControlledComp,
      controlledIsOpen,
      defaultMode,
      initialSource,
    });

    const { selectedProfile } = useProfilesData();
    const { allModules } = useModulesData();
    const isOnDashboard = location.pathname === '/';
    const effectiveInitialPosition =
      getEffectiveInitialPosition(initialPosition);
    const isOfflineDelivery = allModules.some(moduleData =>
      moduleData.sessions.some(session => session.delivery_type === 'offline')
    );
    const { isInitialLoading } = useInitialHistoryLoader(
      leadId,
      isOpen,
      currentSource
    );
    const { handleToggleOpen } = useBhanzuBuddyToggleOpen({
      isOpen,
      isControlledComp,
      onToggleOpen,
      showQuickChallenge,
      setShowQuickChallenge,
      setChatState,
      hasScrolledOnOpen,
      setInternalIsOpen,
    });
    const {
      position,
      isDragging,
      containerRef,
      handleMouseDown,
      handleTouchStart,
      wasDragged,
    } = useDraggable({
      initialPosition: effectiveInitialPosition,
      disabled: disabled || isOpen || isMobile,
    });
    const { isListening, speechError, handleVoiceRecord } = useBhanzuBuddyVoice(
      {
        setMessage,
      }
    );
    const { pendingGuideQuestionRef, handleSwitchToGuide } =
      useBhanzuBuddySwitchToGuide({
        setCurrentMode,
        setCurrentSource,
      });
    const {
      messages,
      isLoading,
      isHistoryLoading,
      onSendMessage: apiSendMessage,
      onSendQuickChallengeAnswer,
      appendBuddyMessage,
      onFeedback,
      onGuideMe,
      refetchWithSource,
      handleGuideFlow,
    } = useOptionalBhanzuBuddyChat(
      leadId
        ? {
            leadId,
            source: currentSource,
            onMessageSent,
            onError,
            onSwitchToGuide: handleSwitchToGuide,
          }
        : undefined
    );

    useBhanzuBuddyEffects({
      chatContainerRef,
      messages,
      isOpen,
      hasScrolledOnOpen,
      message,
      prevMessageRef,
      handleToggleOpen,
      quickChallengeContextRef,
      appendBuddyMessage,
      isControlled: isControlledComp,
      setChatState,
    });

    const { resetIdleTimer, chatHistoryQuery } = useQuickChallenge({
      isOnDashboard,
      hasUserInteracted,
      isOfflineDelivery,
      showQuickChallenge,
      quickChallengeSnoozeUntil,
      chatState,
      isOpen,
      setShowQuickChallenge,
      quickChallengeContextRef,
      leadId,
      enabled: enableQuickChallenge,
    });

    useBhanzuBuddyUserInteraction({
      hasUserInteracted,
      setHasUserInteracted,
    });

    const { handleSendMessage, handleKeyPress, handleChatInputSend } =
      useBhanzuBuddyHandlers({
        leadId,
        message,
        setMessage,
        quickChallengeContextRef,
        onSendQuickChallengeAnswer,
        apiSendMessage,
        prevMessageRef,
      });

    const { handleToggleMode } = useBhanzuBuddyModeHandlers({
      setCurrentMode,
      setCurrentSource,
      hasScrolledOnOpen,
      leadId,
      refetchWithSource,
    });

    useBhanzuBuddyGuideFlow({
      pendingGuideQuestionRef,
      currentMode,
      currentSource,
      handleGuideFlow: handleGuideFlow
        ? (q: string) => {
            Promise.resolve()
              .then(() => handleGuideFlow(q))
              .catch(() => {});
          }
        : undefined,
      leadId,
    });

    const { handleQuickChallengeClose } = useBhanzuBuddyQuickChallengeHandlers({
      setShowQuickChallenge,
      setQuickChallengeSnoozeUntil,
      resetIdleTimer,
      quickChallengeContextRef,
    });

    if (chatState === 'closed' && !isOpen) {
      const shouldShowQuickChallenge =
        (hasUserInteracted || isOfflineDelivery) &&
        !orientation &&
        showQuickChallenge.open;

      return (
        <ClosedView
          position={position}
          containerRef={containerRef}
          handleMouseDown={handleMouseDown}
          handleTouchStart={handleTouchStart}
          isDragging={isDragging}
          wasDragged={wasDragged}
          handleToggleOpen={handleToggleOpen}
          message={message}
          setMessage={setMessage}
          handleKeyPress={handleKeyPress}
          placeholder={placeholder}
          disabled={disabled}
          handleVoiceRecord={() => {
            void handleVoiceRecord();
          }}
          isListening={isListening}
          speechError={speechError}
          handleSendMessage={handleSendMessage}
          showQuickChallenge={{
            ...showQuickChallenge,
            open: shouldShowQuickChallenge,
          }}
          setShowQuickChallenge={setShowQuickChallenge}
          handleQuickChallengeClose={handleQuickChallengeClose}
          quickChallengeContextRef={quickChallengeContextRef}
          appendBuddyMessage={appendBuddyMessage}
          selectedProfile={selectedProfile}
          chatHistoryQuery={chatHistoryQuery}
          ref={ref}
        />
      );
    }

    return (
      <OpenView
        chatState={chatState}
        handleToggleOpen={handleToggleOpen}
        chatInterfaceRef={chatInterfaceRef}
        className={className}
        props={props}
        currentMode={currentMode}
        handleToggleMode={handleToggleMode}
        chatContainerRef={chatContainerRef}
        messages={messages}
        isInitialLoading={isInitialLoading}
        isHistoryLoading={isHistoryLoading}
        onFeedback={onFeedback}
        onGuideMe={onGuideMe}
        isBraveBrowser={isBraveBrowser()}
        handleChatInputSend={handleChatInputSend}
        isLoading={isLoading}
        message={message}
        setMessage={setMessage}
        ref={ref}
      />
    );
  }
);

BhanzuBuddy.displayName = 'BhanzuBuddy';

export { BhanzuBuddy };
